import React, { useState } from 'react';
import PropTypes, { node, number } from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDoubleUp,
  faAngleDoubleDown
} from '@fortawesome/free-solid-svg-icons';
import { useCurrentWidth } from '@shoflo/polymer/hooks/useCurrentWidth/useCurrentWidth';

import BannerTemplateListPropType from 'components/BannerTemplateListPropType';
import BannerTemplatePropType from 'components/BannerTemplatePropType';
import ExampleBanner from './ExampleBanner';

import {
  Wrapper,
  StyledToggleButton,
  TemplateWrapper,
  StyledRadioLabel
} from './BannerTemplateChooser.styled';
import { LG_THRESHOLD, XL_THRESHOLD } from 'constants/ScreenDimensions';
import { PRESENTER_BANNER } from 'constants/Banners';

const BannerTemplateChooser = ({
  value,
  options,
  onChange,
  formValues,
  showRadioTitles
}) => {
  const [showOptions, setShowOptions] = useState(!value);

  if (!showOptions) {
    return (
      <Wrapper noPaddingBottom={showRadioTitles}>
        <Template
          selected
          formValues={formValues}
          showRadioTitles={showRadioTitles}
        >
          {value}
        </Template>
        {value && !showRadioTitles && (
          <OptionsToggleButton
            onClick={() => setShowOptions(true)}
            isShowingOptions={showOptions}
          />
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper noPaddingBottom={showRadioTitles}>
      {options
        .filter(template => template.type !== PRESENTER_BANNER)
        .map(template => (
          <Template
            onClick={onChange.bind(null, template)}
            key={`banner-template-li-${template._id}`}
            selected={value && template._id === value._id}
            formValues={formValues}
            showRadioTitles={showRadioTitles}
          >
            {template}
          </Template>
        ))}

      {value && !showRadioTitles && (
        <OptionsToggleButton
          onClick={() => setShowOptions(false)}
          isShowingOptions={showOptions}
        />
      )}
    </Wrapper>
  );
};

BannerTemplateChooser.defaultProps = {
  showRadioTitles: false
};

BannerTemplateChooser.propTypes = {
  options: BannerTemplateListPropType,
  value: BannerTemplatePropType,
  onChange: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  showRadioTitles: PropTypes.bool
};

const OptionsToggleButton = ({ onClick, isShowingOptions }) => {
  const handleClick = e => {
    e.preventDefault();
    onClick();
  };

  return (
    <StyledToggleButton className="btn" onClick={handleClick}>
      <OptionIcon
        icon={isShowingOptions ? faAngleDoubleUp : faAngleDoubleDown}
      />
      {isShowingOptions ? 'Collapse' : 'See all banners'}
    </StyledToggleButton>
  );
};

OptionsToggleButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isShowingOptions: PropTypes.bool.isRequired
};

/**
 * Option icon composer.
 *
 * @param icon
 * @returns {*}
 * @constructor
 */
const OptionIcon = ({ icon }) => {
  return <FontAwesomeIcon icon={icon} style={{ color: '#6E7582' }} />;
};

OptionIcon.propTypes = {
  icon: PropTypes.object.isRequired
};

const Template = ({
  children,
  selected,
  onClick,
  formValues,
  showRadioTitles
}) => {
  const width = useCurrentWidth();

  return (
    <TemplateWrapper onClick={onClick} width={width}>
      <div className="radio-container">
        <input
          type="radio"
          checked={selected}
          readOnly
          id={`${children._id}-radio`}
        />
        {showRadioTitles && (
          <StyledRadioLabel htmlFor="radio-template-chooser">
            Banner 01
          </StyledRadioLabel>
        )}
      </div>
      <div className="banner">
        <Scaler width={width}>
          <ExampleBanner formValues={formValues}>{children}</ExampleBanner>
        </Scaler>
      </div>
    </TemplateWrapper>
  );
};

Template.propTypes = {
  children: BannerTemplatePropType,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  formValues: PropTypes.object,
  showRadioTitles: PropTypes.bool
};

/**
 * Renders a smaller version of the banner that fits nicely on the space
 * available on the banner template chooser.
 *
 * @param width
 * @param children
 * @returns {*}
 * @constructor
 */
const Scaler = ({ width, children }) => {
  let ratio = 0.448;

  if (width < XL_THRESHOLD) {
    ratio = 0.273;
  }
  if (width < LG_THRESHOLD) {
    ratio = 0.195;
  }

  const height = 227 * ratio; // scale height against default banner height

  return (
    <figure style={{ width: '1920px', height: `${height}px` }}>
      <div style={{ transform: `scale(${ratio})`, transformOrigin: 'left 0' }}>
        {children}
      </div>
    </figure>
  );
};

Scaler.propTypes = {
  width: number.isRequired,
  children: node.isRequired
};

export default BannerTemplateChooser;
